import React, { useEffect, useState, useMemo } from "react";
import { useRoom } from "./RoomProvider";
import { apiClient } from "../../lib/api";
import { ZoneBadge, SameZoneToggle } from "../../components/ZoneBadge";
import { useZones } from "../../hooks/useZones";

/**
 * TpOfferDrawer — the restored TP marketplace gate, in-panel (no modal,
 * matches the Floor's convention). Fans a trip out to N TPs via the same
 * canonical `POST /tpt/trips/{id}/offer` endpoint Live Ops uses.
 */
export function TpOfferDrawer({ trip, onClose, onOffered }) {
  const { offerToTps } = useRoom();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [ttl, setTtl] = useState(10);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sameZoneOnly, setSameZoneOnly] = useState(false);
  const { zoneForAddress } = useZones();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get("/tpt/providers");
        if (alive) setProviders(Array.isArray(data) ? data : []);
      } catch { if (alive) setError("couldn't load TPs"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = (tpId) => {
    setSelected((s) => s.includes(tpId) ? s.filter((x) => x !== tpId) : [...s, tpId]);
  };

  const send = async () => {
    if (selected.length === 0) return;
    setSending(true);
    setError(null);
    const res = await offerToTps(trip.trip_id, { tp_ids: selected, ttl_minutes: ttl });
    setSending(false);
    if (res.ok) onOffered?.();
    else setError(res.error || "offer failed");
  };

  const pickupZone = useMemo(() => zoneForAddress(trip?.pickup_address), [zoneForAddress, trip]);

  // Zone grouping: in-zone TPs surface first (previously no distance/zone
  // ordering at all — a flat, unranked list).
  const sortedProviders = useMemo(() => {
    const withZone = providers.map((p) => ({ ...p, _same_zone: !!(pickupZone && p.zone_id && p.zone_id === pickupZone.zone_id) }));
    return withZone
      .filter((p) => !sameZoneOnly || p._same_zone)
      .sort((a, b) => (a._same_zone === b._same_zone ? 0 : a._same_zone ? -1 : 1));
  }, [providers, pickupZone, sameZoneOnly]);

  return (
    <div className="ws-tp-drawer" data-testid={`tp-offer-drawer-${trip.trip_id}`}>
      {loading ? (
        <div className="ws-rec-text">loading eligible TPs…</div>
      ) : providers.length === 0 ? (
        <div className="ws-rec-text">no TPs on file yet.</div>
      ) : (
        <>
          {pickupZone && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
              <SameZoneToggle checked={sameZoneOnly} onChange={setSameZoneOnly} testId={`tp-offer-same-zone-${trip.trip_id}`} />
            </div>
          )}
          <div className="ws-tp-list">
            {sortedProviders.map((p) => (
              <label key={p.tp_id} className={`ws-tp-row ${selected.includes(p.tp_id) ? "on" : ""}`}>
                <input type="checkbox" checked={selected.includes(p.tp_id)} onChange={() => toggle(p.tp_id)}
                       data-testid={`tp-offer-checkbox-${p.tp_id}`} />
                <span>{p.name}</span>
                <ZoneBadge zoneName={p.zone_name} sameZone={p._same_zone} style={{ marginLeft: "auto" }} />
              </label>
            ))}
            {sortedProviders.length === 0 && <div className="ws-rec-text">no TPs in this zone — turn off "same zone only" to see everyone</div>}
          </div>
        </>
      )}
      <div className="ws-tp-ttl">
        <span>expires in</span>
        <input type="number" min={1} max={60} value={ttl}
               onChange={(e) => setTtl(Number(e.target.value) || 10)} />
        <span>min</span>
      </div>
      {error && <div className="ws-tp-error">{error}</div>}
      <div className="ws-tp-actions">
        <button className="ws-primary" disabled={sending || selected.length === 0}
                onClick={send} data-testid={`tp-offer-send-${trip.trip_id}`}>
          {sending ? "offering…" : `offer to ${selected.length || ""} TP${selected.length === 1 ? "" : "s"}`}
        </button>
        <button className="ws-verb" onClick={onClose} data-testid={`tp-offer-cancel-${trip.trip_id}`}>cancel</button>
      </div>
    </div>
  );
}

/**
 * TpOfferChip — live countdown for one TP inside an in-flight offer pool.
 * Purely presentational; the pool itself is server-authored.
 */
export function TpOfferChip({ entry }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  // eslint-disable-next-line no-unused-vars
  const _tick = tick;
  const remaining = entry.expires_at
    ? Math.max(0, Math.round((new Date(entry.expires_at).getTime() - Date.now()) / 1000))
    : null;
  const mmss = remaining != null
    ? `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`
    : "—";
  const status = entry.status || "pending";
  return (
    <div className={`ws-tp-chip ws-tp-chip--${status}`} data-testid={`tp-offer-chip-${entry.tp_id}`}>
      <span className="ws-tp-chip-name">{entry.tp_name || entry.tp_id}</span>
      <span className="ws-tp-chip-status">{status}</span>
      {status === "pending" && <span className="ws-tp-chip-ttl">{remaining === 0 ? "expired" : mmss}</span>}
    </div>
  );
}
